import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { InputMask } from "@react-input/mask";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Button } from "../../shared";
import { changePhone } from "../../../services/personal";

interface Props {
  open: boolean;
  onClose: () => void;
  currentPhone: string | null;
}

const PHONE_RE = /^\+\d{8,15}$/;

/**
 * Bottom/right-drawer смены телефона: два поля (новый номер + PIN) на
 * одном экране, submit — один запрос. SMS-провайдера у нас нет,
 * поэтому доказательством владения аккаунтом служит PIN.
 */
export function PhoneChangeDrawer({ open, onClose, currentPhone }: Props) {
  const { t } = useTranslation(["profile", "onboarding", "errors"]);
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");

  useEffect(() => {
    if (!open) {
      setPhone("");
      setPin("");
    }
  }, [open]);

  const changeMut = useMutation({
    mutationFn: (payload: { newPhone: string; pin: string }) =>
      changePhone(payload.newPhone, payload.pin),
    onSuccess() {
      enqueueSnackbar(
        t("profile:personal_data.phone_changed", {
          defaultValue: "Телефон обновлён",
        }),
        { variant: "success" },
      );
      qc.invalidateQueries(["user-info"]);
      onClose();
    },
    onError(err) {
      enqueueSnackbar(pickMessage(err), { variant: "error" });
    },
  });

  const normalizedPhone = phone.replace(/[^\d+]/g, "");
  const isPhoneValid = useMemo(
    () => PHONE_RE.test(normalizedPhone) && normalizedPhone !== currentPhone,
    [normalizedPhone, currentPhone],
  );
  const isPinValid = /^\d{4}$/.test(pin);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100015 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "100%",
          bgcolor: "background.default",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          padding:
            "calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 20px)",
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "20px",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ color: "text.primary" }}
          aria-label="close"
        >
          <Icon icon="solar:alt-arrow-left-linear" width={22} height={22} />
        </IconButton>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.2,
          }}
        >
          {t("profile:personal_data.change_phone", {
            defaultValue: "Смена телефона",
          })}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: "14px",
          color: "text.secondary",
          marginBottom: "16px",
          lineHeight: 1.5,
        }}
      >
        {t("profile:personal_data.change_phone_hint", {
          defaultValue:
            "Введите новый номер телефона и ваш PIN для подтверждения.",
        })}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              marginBottom: "6px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t("profile:personal_data.phone", { defaultValue: "Телефон" })}
          </Typography>
          <InputMask
            component={TextField}
            mask="+7 (___) ___-__-__"
            replacement={{ _: /\d/ }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+7 900 000-00-00"
            type="tel"
            fullWidth
            InputProps={{
              sx: {
                borderRadius: "14px",
                bgcolor: "secondary.main",
              },
            }}
            inputProps={{ inputMode: "tel", autoComplete: "tel" }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              marginBottom: "6px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t("profile:personal_data.pin", { defaultValue: "PIN-код" })}
          </Typography>
          <TextField
            fullWidth
            placeholder="••••"
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            InputProps={{
              sx: {
                borderRadius: "14px",
                bgcolor: "secondary.main",
                fontSize: "20px",
                letterSpacing: "10px",
                "& input": { textAlign: "center" },
              },
            }}
            inputProps={{ inputMode: "numeric", maxLength: 4 }}
          />
        </Box>
      </Box>

      <Button
        onClick={() =>
          changeMut.mutate({ newPhone: normalizedPhone, pin })
        }
        disabled={!isPhoneValid || !isPinValid || changeMut.isLoading}
        sx={{ marginTop: "24px" }}
      >
        {changeMut.isLoading ? (
          <CircularProgress size={22} sx={{ color: "#fff" }} />
        ) : (
          t("profile:personal_data.save", { defaultValue: "Сохранить" })
        )}
      </Button>
    </Drawer>
  );
}

function pickMessage(err: unknown): string {
  const raw =
    (err as { response?: { data?: { message?: string | string[] } } })?.response
      ?.data?.message;
  const msg = Array.isArray(raw) ? raw.join(", ") : raw;
  return msg || "Не удалось выполнить действие";
}
